import Axios from 'axios';
import store from '@/store';
import Vue from 'vue';
import i18n from '@/i18n';
import { friendlyError } from '@/utils/errorMessage';
import { resolveEffectiveLanguage } from '@/utils/language';
import { ALL_BRANCHES } from '@/utils/branchScope';

// Router is injected at app startup via setRouter() to avoid a circular
// dependency (routes import components which import services which import http).
let router;
export const setRouter = (r) => { router = r; };

const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let name = 'Unknown Browser';
  let version = 'Unknown Version';

  if (ua.indexOf('Firefox') > -1) {
    name = 'Mozilla Firefox';
    version = (ua.match(/Firefox\/(\d+\.\d+)/) || [])[1] || version;
  } else if (ua.indexOf('Edg') > -1) {
    name = 'Microsoft Edge';
    version = (ua.match(/Edg\/(\d+\.\d+)/) || [])[1] || version;
  } else if (ua.indexOf('Chrome') > -1) {
    name = 'Google Chrome';
    version = (ua.match(/Chrome\/(\d+\.\d+)/) || [])[1] || version;
  } else if (ua.indexOf('Safari') > -1) {
    name = 'Apple Safari';
    version = (ua.match(/Version\/(\d+\.\d+)/) || [])[1] || version;
  } else if (ua.indexOf('OPR') > -1 || ua.indexOf('Opera') > -1) {
    name = 'Opera';
    version = (ua.match(/OPR\/(\d+\.\d+)/) || [])[1] || version;
  } else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident') > -1) {
    name = 'Microsoft Internet Explorer';
    version = (ua.match(/(MSIE |rv:)(\d+\.\d+)/) || [])[2] || version;
  }

  return { name, version };
};

const browserInfo = getBrowserInfo();

const http = Axios.create();

http.interceptors.request.use((req) => {
  req.headers.TokenAuth = store.state && store.state.auth.userData
    ? store.state.auth.userData.token
    : '';
  req.headers.TimingOffset = new Date().getTimezoneOffset();
  req.headers['Access-Control-Allow-Origin'] = '*';
  if (!(req.data instanceof FormData)) {
    req.headers['Content-Type'] = 'application/json';
  }
  req.headers['Custom-User-Agent'] = `Browser: ${browserInfo.name}, Version: ${browserInfo.version}`;
  req.headers.IpAddress = store.state && store.state.auth.ipAddress;

  // Active branch for per-branch data isolation. The backend ignores it for all-branch users and
  // single-branch tenants, so sending it unconditionally is safe.
  //
  // A caller that set the header itself wins: reports scope one request to a branch, to several
  // ("5,6"), or to all of them, without moving the switcher the rest of the app follows.
  //
  // No branch pinned means the operator chose the combined view, and that has to be said out loud:
  // an absent header is indistinguishable from one the backend could not parse, and it used to pin
  // a branch-scoped operator to a single branch instead of spanning the ones they hold.
  const activeBranchId = store.state && store.state.auth && store.state.auth.activeBranchId;
  if (!req.headers['X-Branch-Id']) {
    req.headers['X-Branch-Id'] = activeBranchId || ALL_BRANCHES;
  }

  const { languageCode } = resolveEffectiveLanguage(
    store.state && store.state.auth && store.state.auth.userData,
    store.state && store.state.settings && store.state.settings.tenantSetting,
  );
  req.headers['Accept-Language'] = languageCode;

  return req;
});

let isRedirectingToLogout = false;

http.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only force a logout when there's actually a session to end. A 401 while
      // logged out (e.g. a pre-auth lookup on the login page) must not bounce
      // through /logout — that remounts the login form and wipes typed input.
      const isLoggedIn = !!(store.state && store.state.auth
        && store.state.auth.userData && store.state.auth.userData.token);
      if (isLoggedIn && !isRedirectingToLogout && router) {
        isRedirectingToLogout = true;
        router.push({ name: 'logout', params: { sessionOutofDate: true } })
          .catch(() => {})
          .finally(() => { isRedirectingToLogout = false; });
      }
      return Promise.reject(error);
    } else if (error.response && error.response.status === 404) {
      // handled by caller
    } else if (error.response) {
      // A last line of defence, deliberately a beat behind: the caller's own catch runs first and,
      // if it reports the failure, marks it handled — that message names what the user was doing,
      // which this layer cannot know. Only an unclaimed failure gets spoken for here, so nothing
      // fails silently and nothing is announced twice.
      error.userMessage = friendlyError(error, i18n.t.bind(i18n), { locale: i18n.locale });
      setTimeout(() => {
        if (error.handled || (error.config && error.config.suppressErrorToast)) return;
        Vue.$toast.error(error.userMessage);
        error.toastShown = true;
      }, 0);
    }
    return Promise.reject(error);
  },
);

const apiUrl = (path) => {
  const base = (store.state && store.state.settings && store.state.settings.apiURL) || '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
};

export const api = {
  get: (path, params, config = {}) => http.get(apiUrl(path), { ...config, params }),
  delete: (path, params, config = {}) => http.delete(apiUrl(path), { ...config, params }),
  post: (path, data, config = {}) => http.post(apiUrl(path), data, config),
  put: (path, data, config = {}) => http.put(apiUrl(path), data, config),
  patch: (path, data, config = {}) => http.patch(apiUrl(path), data, config),
};

export const unwrap = (promise) => promise.then((res) => res.data);

// Build URLSearchParams with one key repeated per array value.
// Used for bulk endpoints that expect ids=1&ids=2&ids=3
export const repeatedParams = (key, values) => {
  const usp = new URLSearchParams();
  (values || []).forEach((v) => usp.append(key, v));
  return usp;
};

export const fetchIpAddress = () => {
  fetch('https://www.cloudflare.com/cdn-cgi/trace')
    .then((response) => response.text())
    .then((data) => {
      store.commit('changeIPAddress', data.split('\n')[2].replace('ip=', ''));
    })
    .catch(() => {});
};

export default http;
